import type {
  FC,
  MouseEvent,
  ReactElement,
  RefObject,
  TouchEvent
} from 'react';
import { Component, cloneElement, createRef } from 'react';
import styled from 'styled-components';
import type { AutoSizeValue } from '@elfsight/configurator-contracts';
import { uuid } from '@elfsight/lib-utils';
import classNames from 'classnames';
import { gsap } from 'gsap';
import { CombinedNavigation } from './combined-navigation';
import { Navigation } from './Navigation';
import { Pagination } from './pagination/Pagination';
import type {
  NavigationCustomIcons,
  PaginationType,
  SliderColors
} from './pagination/types';
import { Slide } from './Slide';
import { sliderContext } from './SliderContext';

interface SliderProps {
  animationType: string;
  appClassnamePrefix: string;
  arrows: boolean;
  arrowsPosition: string;
  autoplay: boolean;
  autoplayDelay: number;
  autoplayPauseOnHover: boolean;
  autoplayProgressBar: boolean;
  children: ReactElement[];
  colors: SliderColors;
  className: string;
  customIcons: NavigationCustomIcons;
  customPagination: FC | null;
  drag: boolean;
  itemClassName: string;
  mobileDrag: boolean;
  navigationClassName: string;
  loop: boolean;
  onTranslateEnd: () => void;
  onTranslateStart: () => void;
  paginationAlign: string;
  paginationClassName: string;
  paginationType: PaginationType;
  paginationPosition: string;
  slidesPerView: number;
  speed: number;
  height?: AutoSizeValue;
  borderRadius?: number;
  onCombinedNavigationPaginationHeightChange?: (newHeight: number) => void;
  shouldGoToFirstSlideWhenSlidesChange?: boolean;
  shouldUseCombinedNavigation?: boolean;
}

function getMouseEventCoords(
  event:
    | globalThis.MouseEvent
    | globalThis.TouchEvent
    | MouseEvent<HTMLDivElement>
    | TouchEvent<HTMLDivElement>
) {
  if ('screenX' in event && 'screenY' in event) {
    return {
      x: event.screenX,
      y: event.screenY
    };
  } else {
    return {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY
    };
  }
}

const sliderId = uuid();

export class Slider extends Component<SliderProps> {
  static defaultProps = {
    arrows: true,
    drag: true,
    mobileDrag: true,
    speed: 0.6,
    loop: false,
    autoplayDelay: 0,
    autoplayPauseOnHover: true,
    paginationType: 'bullets',
    height: { auto: true },
    onTranslateEnd: () => {},
    onTranslateStart: () => {},
    children: [],
    itemClassName: '',
    navigationClassName: '',
    paginationClassName: '',
    animationType: 'slide',
    paginationPosition: 'inside',
    paginationAlign: 'left',
    customPagination: null,
    colors: {
      arrows: {
        color: '#fff',
        background: '#111',
        hover: {
          color: '#111',
          background: '#8e8e8e'
        }
      },
      pagination: {
        color: '#eee',
        active: 'red'
      }
    },
    arrowsPosition: 'inside',
    customIcons: {
      prev: null,
      next: null
    },
    className: ''
  };

  static detectLeftButton(
    event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>
  ) {
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      return false;
    }

    if ('buttons' in event) {
      return event.buttons === 1;
    }

    const isClick = event.type === 'click';

    if ('button' in event) {
      return event.button === 1 || isClick;
    }

    return isClick;
  }

  static getDerivedStateFromProps({
    children,
    paginationType,
    autoplayDelay,
    speed,
    loop
  }: {
    children: ReactElement[];
    paginationType: string;
    autoplayDelay: number;
    speed: number;
    loop: boolean;
  }) {
    const clones = [
      ...children.map((item) =>
        cloneElement(item, { cloned: true.toString() })
      ),
      ...children,
      ...children.map((item) => cloneElement(item, { cloned: true.toString() }))
    ];

    return {
      realSlides: [...children],
      slides: loop ? clones : [...children],
      $realSlides: [...children],
      pagination: paginationType !== 'none',
      autoplay: autoplayDelay > 0,
      autoplayDelay: autoplayDelay * 1000,
      autoplayPaused: !(autoplayDelay > 0),
      autoplayEnabled: autoplayDelay > 0,
      currentSpeed: speed,
      loopEnabled: loop,
      loop
    };
  }

  slider: RefObject<HTMLDivElement>;
  sliderInner: RefObject<HTMLDivElement>;
  loopCircle: number;
  isTranslate: boolean;
  slidesRefs: RefObject<HTMLDivElement>[];
  autoplayTimeout: NodeJS.Timeout | undefined;
  transitionTimeout: NodeJS.Timeout | undefined;
  wasAnimating: boolean = false;
  targetIndex: number = 0;
  dragStartX: number = 0;
  dragStartY: number = 0;

  shouldGoToFirstSlideWhenSlidesChange: boolean;
  onCombinedNavigationPaginationHeightChange: (newHeight: number) => void =
    () => {};

  state = {
    slides: [] as ReactElement[],
    autoplayDelay: 0,
    pagination: 'dots',
    realSlides: [],
    $realSlides: [],
    currentIndex:
      this.props.loop && this.props.children?.length
        ? this.props.children.length
        : 0,
    realIndex: 0,
    sliderWidth: null,
    sliderHeight: null,
    diffX: 0,
    diffY: 0,
    minDiff: 3,
    startX: null,
    startY: null,
    verticalMove: false,
    horizontalMove: false,
    translating: false,
    loopEnabled: false,
    isDrag: false,
    isTouched: false,
    autoplay: false,
    autoplayEnabled: false,
    autoplayPaused: false,
    currentSpeed: 0.6,
    slideBy: 0,
    animationNumber: 0,
    animationType: 'slide',
    instant: false,
    isHovered: false
  };

  constructor(props: SliderProps) {
    super(props);

    this.slider = createRef();
    this.sliderInner = createRef();
    this.loopCircle = 0;
    this.isTranslate = true;
    this.slidesRefs = [];

    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.moveNext = this.moveNext.bind(this);
    this.movePrev = this.movePrev.bind(this);
    this.hasPrev = this.hasPrev.bind(this);
    this.hasNext = this.hasNext.bind(this);
    this.goTo = this.goTo.bind(this);
    this.initLoop = this.initLoop.bind(this);
    this.isRealEnd = this.isRealEnd.bind(this);

    this.shouldGoToFirstSlideWhenSlidesChange =
      !!props.shouldGoToFirstSlideWhenSlidesChange;

    if (props.onCombinedNavigationPaginationHeightChange) {
      this.onCombinedNavigationPaginationHeightChange =
        props.onCombinedNavigationPaginationHeightChange;
    }
  }

  componentDidMount() {
    const { loop } = this.props;
    const { autoplay, slides } = this.state;

    if (autoplay) this.enableAutoplay();
    if (loop && slides.length) this.initLoop();
  }

  componentDidUpdate(prevProps: SliderProps, prevState: typeof this.state) {
    const {
      children: oldSlides,
      loop: loopOld,
      autoplay: autoplayOld,
      autoplayPauseOnHover: autoplayPauseOnHoverOld,
      animationType: animationTypeOld
    } = prevProps;

    const { $realSlides, autoplay, realIndex, currentIndex } = this.state;
    const { autoplayPauseOnHover, loop, animationType } = this.props;

    if (oldSlides.length !== $realSlides.length || loop !== loopOld) {
      if (this.shouldGoToFirstSlideWhenSlidesChange) {
        this.goTo(0, true);
      } else {
        this.goTo($realSlides.length - 1, true);
      }
    }

    if (loop && animationType && animationTypeOld !== animationType) {
      this.goTo(realIndex, true);
    }

    if (animationType === 'fade' && prevState.currentIndex !== currentIndex) {
      this.updateLoopPosition();
    }

    if (autoplayPauseOnHover !== autoplayPauseOnHoverOld) {
      this.stopAutoplay();

      requestAnimationFrame(() => {
        this.enableAutoplay();
      });
    }

    if (autoplay !== autoplayOld) {
      clearTimeout(this.autoplayTimeout);
    }

    if (autoplay) {
      this.enableAutoplay();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.autoplayTimeout);
  }

  translateFunction = (number: number, duration: number, instant = false) => {
    const { animationType } = this.props;
    const { current: sliderInner } = this.sliderInner;

    if (animationType === 'slide') {
      gsap.to(sliderInner, {
        duration: duration ? duration / 1000 : 0,
        xPercent: number
      });
    } else {
      gsap.to(sliderInner, { duration: 0, xPercent: 0 });
    }

    this.setState({
      animationNumber: number,
      instant
    });
  };

  hasPrev() {
    const { currentIndex, loopEnabled } = this.state;

    return currentIndex > 0 || loopEnabled;
  }

  hasNext() {
    const { currentIndex, loopEnabled, slides } = this.state;

    return currentIndex < slides.length - 1 || loopEnabled;
  }

  isRealBeginning() {
    const { currentIndex, loopEnabled, $realSlides } = this.state;

    return currentIndex <= $realSlides.length - 1 && loopEnabled;
  }

  isRealEnd() {
    const { currentIndex, loopEnabled, $realSlides } = this.state;

    return currentIndex >= $realSlides.length * 2 && loopEnabled;
  }

  movePrev(instant?: boolean) {
    if (!this.hasPrev()) {
      return;
    }

    const { currentIndex, realIndex, loopEnabled, $realSlides } = this.state;
    const { slidesPerView, animationType } = this.props;
    const realCount = $realSlides.length;

    if (loopEnabled) {
      if (animationType === 'fade') {
        const newRealIndex = (realIndex - 1 + realCount) % realCount;
        const newCurrentIndex = realCount + newRealIndex;

        this.setState(
          {
            currentIndex: newCurrentIndex,
            realIndex: newRealIndex
          },
          () => this.translate(instant)
        );

        return;
      }

      if (currentIndex < realCount) {
        const safeIndex = currentIndex + realCount;
        const safeRealIndex = (realIndex + realCount) % realCount;

        if (this.sliderInner.current) {
          gsap.killTweensOf(this.sliderInner.current);
          gsap.set(this.sliderInner.current, {
            xPercent: -(100 / slidesPerView) * safeIndex
          });
        }
        this.setState(
          {
            currentIndex: safeIndex - 1,
            realIndex: (safeRealIndex - 1 + realCount) % realCount
          },
          () => this.translate(instant)
        );

        return;
      }
    }

    this.setState(
      {
        currentIndex: currentIndex - 1,
        realIndex: loopEnabled
          ? (realIndex - 1 + realCount) % realCount
          : realIndex - 1
      },
      () => this.translate(instant)
    );
  }

  moveNext(instant?: boolean) {
    if (!this.hasNext()) {
      return;
    }

    const { currentIndex, realIndex, loopEnabled, $realSlides } = this.state;
    const { slidesPerView, animationType } = this.props;
    const realCount = $realSlides.length;

    if (loopEnabled) {
      if (animationType === 'fade') {
        const newRealIndex = (realIndex + 1) % realCount;
        const newCurrentIndex = realCount + newRealIndex;

        this.setState(
          {
            currentIndex: newCurrentIndex,
            realIndex: newRealIndex
          },
          () => this.translate(instant)
        );

        return;
      }

      if (currentIndex >= realCount * 2) {
        const safeIndex = currentIndex - realCount;
        const safeRealIndex = realIndex % realCount;

        if (this.sliderInner.current) {
          gsap.killTweensOf(this.sliderInner.current);
          gsap.set(this.sliderInner.current, {
            xPercent: -(100 / slidesPerView) * safeIndex
          });
        }

        this.setState(
          {
            currentIndex: safeIndex + 1,
            realIndex: (safeRealIndex + 1) % realCount
          },
          () => this.translate(instant)
        );

        return;
      }
    }

    this.setState(
      {
        currentIndex: currentIndex + 1,
        realIndex: loopEnabled ? (realIndex + 1) % realCount : realIndex + 1
      },
      () => this.translate(instant)
    );
  }

  goTo(index?: number, instant = false) {
    const {
      slideBy,
      realIndex,
      $realSlides,
      loopEnabled,
      diffX,
      minDiff,
      isTouched,
      currentIndex
    } = this.state;

    if (Math.abs(diffX) < minDiff && isTouched) {
      this.setState(
        {
          currentIndex: currentIndex,
          realIndex: realIndex
        },
        () => this.translate(instant)
      );
      return;
    }

    let currentIndexNew = currentIndex;
    let realIndexNew = realIndex;
    const slideCount = $realSlides.length;

    if (loopEnabled) {
      if (index === undefined) {
        currentIndexNew = currentIndex + slideBy;
        realIndexNew =
          (((realIndex + slideBy) % slideCount) + slideCount) % slideCount;
      } else {
        const candidates = [index, index + slideCount, index + slideCount * 2];

        const closest = candidates.reduce((prev, curr) =>
          Math.abs(curr - currentIndex) < Math.abs(prev - currentIndex)
            ? curr
            : prev
        );

        currentIndexNew = closest;
        realIndexNew = index;
      }
    } else {
      if (index !== undefined) {
        currentIndexNew = index;
        realIndexNew = index;
      } else {
        currentIndexNew = currentIndex + slideBy;
        if (currentIndexNew < 0) {
          currentIndexNew = 0;
        }

        if (currentIndexNew >= slideCount) {
          currentIndexNew = slideCount - 1;
        }
        realIndexNew = currentIndexNew;
      }
    }

    this.setState(
      {
        currentIndex: currentIndexNew,
        realIndex: realIndexNew
      },
      () => this.translate(instant)
    );
  }

  translate(instant?: boolean) {
    const { currentSpeed, currentIndex } = this.state;
    const { slidesPerView } = this.props;
    const number =
      Math.abs(currentIndex) > 0 ? -(100 / slidesPerView) * currentIndex : 0;

    if (!instant) {
      clearTimeout(this.transitionTimeout);
      this.setState({ translating: true });

      this.transitionTimeout = setTimeout(() => {
        this.setState({ translating: false }, () => this.translateEnd());
      }, currentSpeed * 1000);

      this.translateFunction(number, currentSpeed * 1000);
    } else {
      this.setState({ translating: false }, () => this.translateEnd());
      this.translateFunction(number, 1, true);
    }
  }

  translateStart() {
    const { onTranslateStart } = this.props;
    this.isTranslate = true;
    this.setState({ isDrag: true });

    onTranslateStart();
  }

  translateEnd() {
    const { loopEnabled, isHovered } = this.state;
    const { onTranslateEnd } = this.props;
    this.isTranslate = false;
    this.setState({ isDrag: false });

    if (!isHovered && !loopEnabled) {
      this.tickAutoplay(isHovered);
    }

    if (loopEnabled && !this.isRealEnd()) {
      this.tickAutoplay(isHovered);
      this.updateLoopPosition();
    } else if (loopEnabled && this.isRealEnd()) {
      this.updateLoopPosition();
    }

    onTranslateEnd();
  }

  handleMouseDown(
    event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>
  ) {
    const { $realSlides, currentIndex, loopEnabled, realIndex } = this.state;
    const { drag, mobileDrag, animationType, slidesPerView } = this.props;
    const { type } = event;

    if (
      (Slider.detectLeftButton(event) && drag && $realSlides.length > 1) ||
      (type === 'touchstart' && mobileDrag)
    ) {
      clearTimeout(this.transitionTimeout);
      this.stopAutoplay();

      const { x: rawX, y: rawY } = getMouseEventCoords(event);
      let startX = rawX;
      const sliderWidth = this.slider.current?.offsetWidth ?? 1;
      let newCurrentIndex = currentIndex;
      let newRealIndex = realIndex;

      if (this.sliderInner.current) {
        this.wasAnimating = gsap.isTweening(this.sliderInner.current);
        if (this.wasAnimating) {
          this.targetIndex = currentIndex;
        }

        gsap.killTweensOf(this.sliderInner.current);
        const itemWidth = 100 / slidesPerView;

        if (animationType === 'fade') {
          gsap.set(this.sliderInner.current, { xPercent: 0 });
        } else {
          const currentXPercent = Number(
            gsap.getProperty(this.sliderInner.current, 'xPercent')
          );
          const visualIndex = -currentXPercent / itemWidth;

          if (loopEnabled) {
            const count = $realSlides.length;
            let normalizedIndex = ((visualIndex % count) + count) % count;

            if (normalizedIndex < count) {
              normalizedIndex += count;
            }

            const newXPercent = -normalizedIndex * itemWidth;
            gsap.set(this.sliderInner.current, { xPercent: newXPercent });

            newCurrentIndex = Math.round(normalizedIndex);
            newRealIndex = ((newCurrentIndex % count) + count) % count;
            const idealXPercent = -newCurrentIndex * itemWidth;
            const diffPercent = idealXPercent - newXPercent;
            const diffPixels = (diffPercent / 100) * sliderWidth;

            startX = rawX + diffPixels;
          } else {
            newCurrentIndex = Math.round(visualIndex);
            newRealIndex = newCurrentIndex;
            const idealXPercent = -newCurrentIndex * itemWidth;
            const diffPercent = idealXPercent - currentXPercent;
            const diffPixels = (diffPercent / 100) * sliderWidth;

            startX = rawX + diffPixels;
          }
        }
      }

      this.dragStartX = startX;
      this.dragStartY = rawY;

      this.setState({
        isTouched: true,
        verticalMove: false,
        horizontalMove: false,
        diffX: 0,
        startX,
        startY: rawY,
        sliderWidth,
        sliderHeight: this.slider.current?.offsetHeight ?? 1,
        translating: false,
        currentIndex: newCurrentIndex,
        realIndex: newRealIndex
      });

      window.addEventListener('mousemove', this.handleMouseMove, {
        passive: false
      });
      window.addEventListener('touchmove', this.handleMouseMove, {
        passive: false
      });
      window.addEventListener('mouseup', this.handleMouseUp, {
        passive: false
      });
      window.addEventListener('touchend', this.handleMouseUp, {
        passive: false
      });
    }
  }

  handleMouseMove(
    mouseMoveEvent:
      | globalThis.MouseEvent
      | globalThis.TouchEvent
      | MouseEvent<HTMLDivElement>
      | TouchEvent<HTMLDivElement>
  ) {
    const {
      isTouched,
      startX,
      sliderWidth,
      startY,
      sliderHeight,
      loopEnabled,
      $realSlides,
      realIndex,
      currentIndex,
      animationType
    } = this.state;

    let { diffX, diffY, slideBy, horizontalMove, verticalMove } = this.state;

    const { slidesPerView } = this.props;
    const threshold = 20;

    slideBy = slidesPerView;

    if (isTouched && $realSlides.length > slidesPerView) {
      const { x, y } = getMouseEventCoords(mouseMoveEvent);
      const itemWidth = 100 / slidesPerView;

      const deltaX = startX! - x;
      const deltaY = startY! - y;
      diffX = (deltaX / sliderWidth!) * 100;
      diffY = (deltaY / sliderHeight!) * 100;

      const moveX = Math.abs(deltaX);
      const moveY = Math.abs(deltaY);
      const moveMax = Math.max(moveX, moveY);

      if (!horizontalMove && !verticalMove) {
        if (moveX > moveY) {
          horizontalMove = true;
        } else if (moveY > moveX) {
          verticalMove = true;
        }
      }

      const dragStarted = this.isTranslate;
      if (!dragStarted && moveMax <= threshold) {
        this.setState({
          diffX,
          diffY,
          horizontalMove,
          verticalMove,
          slideBy: 0
        });
        return;
      }

      if (!dragStarted) {
        this.translateStart();
      }

      slideBy =
        diffX /
        (itemWidth === 100 && animationType === 'fade' ? 33.333333 : itemWidth);

      if ($realSlides.length <= slidesPerView) slideBy = 0;

      if (diffX >= 0) {
        slideBy = Math.floor(slideBy + 1);
      } else {
        slideBy = Math.ceil(slideBy - 1);
      }

      if (!verticalMove) {
        if (realIndex + slideBy < 0 && diffX < 0 && !loopEnabled)
          slideBy = -realIndex;

        if (
          realIndex + slideBy >= $realSlides.length &&
          diffX > 0 &&
          !loopEnabled
        ) {
          slideBy = Math.abs(realIndex - $realSlides.length - 1);
        }

        this.updatePosition({
          currentIndex,
          slideBy,
          $realSlides,
          slidesPerView,
          realIndex,
          diffX
        });
      }

      this.setState({
        diffX,
        diffY,
        horizontalMove,
        verticalMove,
        slideBy
      });
    }
  }

  handleMouseUp() {
    const { isTouched, diffX, minDiff, horizontalMove, verticalMove } =
      this.state;

    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('touchmove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('touchend', this.handleMouseUp);

    if (isTouched) {
      this.setState({ isTouched: false });
      const isDrag =
        horizontalMove || verticalMove || Math.abs(diffX) >= minDiff;

      if (isDrag) {
        this.goTo();
      } else if (this.wasAnimating) {
        this.goTo(this.targetIndex);
      }
    }

    this.wasAnimating = false;
    this.setState({ slideBy: 0 });
  }

  updatePosition({
    slidesPerView,
    diffX,
    currentIndex
  }: {
    slideBy: number;
    $realSlides: ReactElement[];
    slidesPerView: number;
    realIndex: number;
    diffX: number;
    currentIndex: number;
  }) {
    const itemWidth = 100 / slidesPerView;
    const percent = -currentIndex * itemWidth - diffX;
    this.translateFunction(percent, 0);
  }

  updateLoopPosition() {
    const { loopEnabled, $realSlides, currentIndex } = this.state;

    if (!loopEnabled) {
      return;
    }

    const count = $realSlides.length;
    const isTooFarLeft = currentIndex < count;
    const isTooFarRight = currentIndex >= count * 2;

    if (isTooFarLeft || isTooFarRight) {
      const newRealIndex = currentIndex % count;
      const newCurrentIndex = count + newRealIndex;

      this.setState(
        {
          currentIndex: newCurrentIndex,
          realIndex: newRealIndex
        },
        () => this.translate(true)
      );
    }
  }

  enableAutoplay() {
    this.startAutoplay(false);
  }

  disableAutoplay() {
    this.setState({ autoplayEnabled: false });

    this.stopAutoplay();
  }

  startAutoplay(isHovered: boolean) {
    const { autoplayEnabled, isTouched, translating } = this.state;
    const { autoplayPauseOnHover } = this.props;

    if (
      (isHovered && autoplayPauseOnHover) ||
      !autoplayEnabled ||
      isTouched ||
      translating
    ) {
      return;
    }

    this.tickAutoplay(isHovered);
  }

  stopAutoplay() {
    const { autoplayEnabled } = this.state;

    if (!autoplayEnabled) {
      return;
    }

    clearTimeout(this.autoplayTimeout);

    this.setState({ autoplayPaused: true });
  }

  tickAutoplay(isHovered: boolean) {
    const {
      autoplayEnabled,
      autoplayPaused,
      autoplayDelay,
      $realSlides,
      translating,
      isTouched,
      isHovered: isHoveredFromState
    } = this.state;
    const { autoplayPauseOnHover } = this.props;

    clearTimeout(this.autoplayTimeout);

    this.autoplayTimeout = setTimeout(() => {
      if (
        !autoplayEnabled ||
        autoplayPaused ||
        (isHovered && autoplayPauseOnHover) ||
        (isHoveredFromState && autoplayPauseOnHover) ||
        $realSlides.length <= 1 ||
        translating ||
        isTouched
      ) {
        return;
      }

      this.moveNext();
    }, autoplayDelay);
  }

  initLoop() {
    const { slides, realSlides } = this.state;
    const { animationType } = this.props;

    if (!slides.length) {
      return;
    }

    // animationType !== 'fade'
    // т.к при fade эффекте при переходе с 1-го на 2-й слайд не срабатывает плавная анимация fade
    // из за instant === true

    this.setState(
      {
        loopEnabled: true,
        loop: true,
        realIndex: 0,
        currentIndex: realSlides.length
      },
      () => this.translate(animationType !== 'fade')
    );
  }

  renderItems() {
    const {
      currentIndex,
      slides,
      isTouched,
      verticalMove,
      currentSpeed,
      slideBy,
      instant,
      diffX,
      minDiff,
      $realSlides
    } = this.state;
    const { slidesPerView, animationType, appClassnamePrefix } = this.props;
    const fadeProgress = Math.min(Math.abs(diffX) / 100, 1);
    const isFadeDrag = animationType === 'fade' && isTouched && !verticalMove;

    return slides.map((item, index) => {
      const {
        props: { cloned }
      } = item;

      const isActive =
        index >= currentIndex && index < currentIndex + slidesPerView;
      const slideNumber = (index % $realSlides.length) + 1;
      let manualOpacity;

      if (isFadeDrag) {
        if (index === currentIndex) {
          manualOpacity = 1 - fadeProgress;
        } else if (diffX > 0 && index === currentIndex + 1) {
          manualOpacity = fadeProgress;
        } else if (diffX < 0 && index === currentIndex - 1) {
          manualOpacity = fadeProgress;
        } else {
          manualOpacity = 0;
        }
      }

      const effectiveIndex =
        isTouched &&
        !verticalMove &&
        Math.abs(diffX) > minDiff &&
        animationType !== 'fade'
          ? currentIndex + slideBy
          : currentIndex;

      return (
        <Slide
          key={`${item.key}${index}`}
          item={item}
          className={classNames(
            {
              [`${appClassnamePrefix}-slider-active`]: isActive,
              cloned: cloned
            },
            `${appClassnamePrefix}-slider-item`
          )}
          width={100 / slidesPerView}
          id={index}
          animation={animationType}
          active={isActive}
          currentIndex={effectiveIndex}
          slideNumber={slideNumber}
          speed={instant ? 0 : currentSpeed}
          manualOpacity={manualOpacity}
          isDragging={isTouched}
        />
      );
    });
  }

  render() {
    const {
      pagination,
      realIndex,
      slideBy,
      isTouched,
      verticalMove,
      $realSlides,
      isHovered,
      loopEnabled,
      autoplayEnabled,
      translating
    } = this.state;

    const {
      slidesPerView,
      arrows,
      arrowsPosition,
      navigationClassName,
      paginationClassName,
      paginationType,
      autoplayDelay,
      animationType,
      height,
      paginationPosition,
      paginationAlign,
      customPagination,
      colors: { pagination: paginationColors, arrows: navigationColors },
      customIcons,
      className,
      autoplayPauseOnHover,
      drag,
      autoplayProgressBar,
      shouldUseCombinedNavigation,
      borderRadius = 0,
      appClassnamePrefix
    } = this.props;

    const isHeightManual =
      height?.auto === false && !!height.customValue && height.customValue > 0;

    const circle = Math.floor((realIndex + slideBy) / $realSlides.length);
    const current =
      Math.abs(realIndex + slideBy) - $realSlides.length * Math.abs(circle);

    const hasSlides = $realSlides.length > slidesPerView;
    const shouldHaveArrows = arrows && hasSlides;
    const shouldHavePagination = !!pagination && hasSlides;

    const navigationSettings = {
      nextSlide: this.moveNext,
      prevSlide: this.movePrev,
      showPrev: this.hasPrev,
      showNext: this.hasNext,
      navigationClassName: navigationClassName,
      colors: navigationColors,
      customIcons: customIcons,
      position: arrowsPosition,
      borderRadius,
      sliderId
    };

    const paginationSettings = {
      slides: $realSlides,
      currentIndex: isTouched && !verticalMove ? Math.abs(current) : realIndex,
      goTo: this.goTo,
      paginationClassName: paginationClassName,
      type: paginationType,
      seconds: autoplayDelay,
      animation: animationType,
      colors: paginationColors,
      customPagination: customPagination,
      autoplayProgressBar: autoplayProgressBar,
      autoplayPauseOnHover: autoplayPauseOnHover,
      autoplayEnabled: autoplayEnabled && !translating && !isTouched,
      loop: loopEnabled,
      position: paginationPosition,
      align: paginationAlign,
      isHovered: isHovered
    };

    return (
      <sliderContext.Provider
        value={{
          isHovered,
          setHovered: (value) => this.setState({ isHovered: value }),
          appClassnamePrefix,
          isDrag: false
        }}
      >
        <sliderContext.Consumer>
          {({ setHovered }) => (
            <StyledSlider
              ref={this.slider}
              role="region"
              aria-roledescription="carousel"
              aria-label="Slider"
              onMouseOver={() => {
                setHovered(true);
                if (autoplayPauseOnHover) this.stopAutoplay();
              }}
              onMouseLeave={() => {
                setHovered(false);
                if (autoplayPauseOnHover) this.startAutoplay(false);
              }}
              _appClassPrefix={appClassnamePrefix}
              _derivedClassname={className}
              _height={isHeightManual ? height.customValue : undefined}
            >
              <Outer
                onMouseDown={(mouseDownEvent) =>
                  this.handleMouseDown(mouseDownEvent)
                }
                onTouchStart={(touchStartEvent) =>
                  this.handleMouseDown(touchStartEvent)
                }
                _appClassPrefix={appClassnamePrefix}
                _borderRadius={borderRadius}
                _isHeightManual={isHeightManual}
              >
                <Inner
                  id={sliderId}
                  ref={this.sliderInner}
                  aria-live={autoplayEnabled ? 'off' : 'polite'}
                  _appClassPrefix={appClassnamePrefix}
                  _drag={drag}
                  _itemWidthPercent={100 / slidesPerView}
                  _isTouched={isTouched}
                  _slidesLength={$realSlides.length}
                  _isHeightManual={isHeightManual}
                >
                  {this.renderItems()}
                </Inner>
              </Outer>

              {!shouldUseCombinedNavigation && shouldHaveArrows && (
                <Navigation {...navigationSettings} />
              )}

              {!shouldUseCombinedNavigation && shouldHavePagination && (
                <Pagination {...paginationSettings} />
              )}

              {shouldUseCombinedNavigation &&
                (shouldHaveArrows || shouldHavePagination) && (
                  <CombinedNavigation
                    navigationSettings={navigationSettings}
                    paginationSettings={paginationSettings}
                    shouldHaveArrows={shouldHaveArrows}
                    shouldHavePagination={shouldHavePagination}
                    onPaginationHeightChange={
                      this.onCombinedNavigationPaginationHeightChange
                    }
                  />
                )}
            </StyledSlider>
          )}
        </sliderContext.Consumer>
      </sliderContext.Provider>
    );
  }
}

interface StyledSliderProps {
  _appClassPrefix: string;
  _derivedClassname: string;
  _height?: number;
}

const StyledSlider = styled.div.attrs<StyledSliderProps>(
  ({ _appClassPrefix, _derivedClassname }) => ({
    className: classNames(`${_appClassPrefix}-slider-slider`, _derivedClassname)
  })
)`
  position: relative;
  user-select: none;
  box-sizing: content-box;
  transition: 0.3s ease all;
  overflow: hidden;
  height: ${({ _height }) => (_height ? `${_height}px` : 'auto')};
`;

interface InnerProps {
  _appClassPrefix: string;
  _drag: boolean;
  _itemWidthPercent: number;
  _isTouched: boolean;
  _slidesLength: number;
  _isHeightManual: boolean;
}

const Inner = styled.div.attrs<InnerProps>(({ _appClassPrefix }) => ({
  className: classNames(`${_appClassPrefix}-slider-inner`)
}))`
  z-index: 1;
  position: relative;
  display: flex;
  transition-property: transform;
  transition: 0s ease all;
  will-change: left;
  backface-visibility: hidden;

  height: ${({ _isHeightManual }) => (_isHeightManual ? '100%' : 'auto')};

  ${({
    _appClassPrefix,
    _drag,
    _itemWidthPercent,
    _isTouched,
    _slidesLength
  }) => `
  ${
    _drag && _slidesLength > 1
      ? _isTouched
        ? 'cursor: grabbing;'
        : 'cursor: grab;'
      : ''
  }

  & .${_appClassPrefix}-slider-item {
    width: ${_itemWidthPercent}%;
  }`}
`;

interface OuterProps {
  _appClassPrefix: string;
  _borderRadius: number;
  _isHeightManual: boolean;
}

const Outer = styled.div.attrs<OuterProps>(({ _appClassPrefix }) => ({
  className: classNames(`${_appClassPrefix}-slider-outer`)
}))`
  display: block;
  overflow: hidden;
  transform: translateZ(0px);
  max-height: 100%;

  ${({ _isHeightManual }) => _isHeightManual && 'height: 100%'};
  ${({ _borderRadius }) => `border-radius: ${_borderRadius}px;`}
`;
